import React, { Component } from 'react';
import { Brain, AlertTriangle, Activity, TrendingDown } from 'lucide-react';
import { motion } from 'framer-motion';
export const HiddenToxicitySection = () => {
  return <section className="py-24 md:py-32 bg-[#050505] relative overflow-hidden" data-id="element-2468" data-mp="22215mnpw7vdq">
      {/* Grid Background */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(20,20,20,0.5)_1px,transparent_1px),linear-gradient(90deg,rgba(20,20,20,0.5)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_70%,transparent_100%)] pointer-events-none" data-id="element-2469" data-mp="1x2oazgss5k0l" />

      {/* Red Glow Accent */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#FF0000]/5 rounded-full blur-[120px] pointer-events-none" data-id="element-2470" data-mp="1ifcv52pkfduo" />

      <div className="max-w-6xl mx-auto px-4 md:px-6 relative z-10" data-id="element-2471" data-mp="2tt70opqrb2w5">
        {/* Section Header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16 md:mb-20" data-id="element-2472" data-mp="2ps56woflzj1k">
          <div className="inline-flex items-center gap-2 mb-6 bg-[#FF0000]/10 border border-[#FF0000]/30 px-4 py-2 rounded-full" data-id="element-2473" data-mp="2971gao6pthxp">
            <Brain size={16} className="text-[#FF0000]" data-id="element-2474" data-mp="1chsc74yenerw" />
            <span className="text-[#FF0000] font-mono text-xs tracking-widest uppercase font-bold" data-id="element-2475" data-mp="1cexuhl6s0826">
              Neurological Research
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6 uppercase tracking-tight leading-none px-4" data-id="element-2476" data-mp="1lg4bdvdc7kvy">
            The <span className="text-[#FF0000]" data-id="element-2477" data-mp="st1ipjvce4dm">Hidden Toxicity</span> Crisis
          </h2>

          <p className="text-base md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed font-mono px-4" data-id="element-2478" data-mp="xruriyzop77g">
            Why your brain can't detect the patterns that are destroying you.
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 mb-16 md:mb-20" data-id="element-2479" data-mp="1w35sawh206y3">
          {/* Left: The Problem */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-2480" data-mp="29wwrbax5s8fk">
            <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 md:p-8 h-full" data-id="element-2481" data-mp="1r283w25gago5">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#FF0000]" data-id="element-2482" data-mp="32iyctt19dmlg" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#FF0000]" data-id="element-2483" data-mp="2xlg1jvzrqvaa" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#FF0000]" data-id="element-2484" data-mp="361f9uk5lj0a0" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#FF0000]" data-id="element-2485" data-mp="3a4bmzvcda46k" />

              <div className="flex items-center gap-3 mb-8" data-id="element-2486" data-mp="2w4ssi5charcq">
                <div className="w-12 h-12 bg-[#FF0000]/10 border border-[#FF0000] flex items-center justify-center flex-shrink-0" data-id="element-2487" data-mp="312c4eylpes1i">
                  <AlertTriangle size={24} className="text-[#FF0000]" data-id="element-2488" data-mp="3ot7unraihf6n" />
                </div>
                <div className="min-w-0" data-id="element-2489" data-mp="3tq7zjyvmkbw6">
                  <div className="text-[10px] font-mono text-gray-500 tracking-widest uppercase" data-id="element-2490" data-mp="3ttc9zdzoo3va">
                    Diagnostic Finding
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-2491" data-mp="3k0eqp5mvaavm">
                    The Problem
                  </h3>
                </div>
              </div>

              <div className="space-y-6" data-id="element-2492" data-mp="gdemq0u8xc17">
                <div data-id="element-2493" data-mp="ge69cm5v7zfc">
                  <div className="text-sm font-mono text-[#FFD700] mb-3 uppercase tracking-wide" data-id="element-2494" data-mp="2nvof2wyjeon8">
                    What You Think:
                  </div>
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-2495" data-mp="2jt1fuazje1g0">
                    "We're just having normal relationship conflicts."
                  </p>
                </div>

                <div className="h-px bg-gradient-to-r from-transparent via-gray-800 to-transparent" data-id="element-2496" data-mp="29xzqpf59gzdf" />

                <div data-id="element-2497" data-mp="37j49gvj3nfgq">
                  <div className="text-sm font-mono text-[#FF0000] mb-3 uppercase tracking-wide flex items-center gap-2" data-id="element-2498" data-mp="32ku9m7mfjqos">
                    <span className="w-2 h-2 bg-[#FF0000] rounded-full animate-pulse flex-shrink-0" data-id="element-2499" data-mp="2xtkkd84smpdf" />
                    Clinical Reality:
                  </div>
                  <p className="text-white text-base md:text-lg leading-relaxed font-medium" data-id="element-2500" data-mp="2st6p6qoaj9wt">
                    You're experiencing systematic emotional drainage through
                    patterns your brain has been conditioned to normalize.
                  </p>
                </div>

                <div className="bg-[#FF0000]/5 border border-[#FF0000]/20 p-4 rounded" data-id="element-2501" data-mp="3swxq68mbid7i">
                  <div className="text-xs font-mono text-[#FF0000] mb-2 uppercase tracking-widest" data-id="element-2502" data-mp="1os99ckw8aiy">
                    ⚠ Critical Insight
                  </div>
                  <p className="text-gray-300 text-sm leading-relaxed font-mono" data-id="element-2503" data-mp="bmzkrfwey0dk">
                    Your nervous system adapts to chronic stress by
                    <span className="text-white font-bold" data-id="element-2504" data-mp="fm8cygbt1q70">
                      {' '}
                      normalizing the abuse
                    </span>
                    . This is called "trauma bonding" or "acclimatization to
                    toxicity."
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right: The Science */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} data-id="element-2505" data-mp="zah8n0ck4eb9">
            <div className="relative bg-[#0a0a0a] border border-gray-800 p-6 md:p-8 h-full" data-id="element-2506" data-mp="398oe5qeo3q96">
              {/* Corner Brackets */}
              <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-gray-600" data-id="element-2507" data-mp="3j4httinonzv6" />
              <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-gray-600" data-id="element-2508" data-mp="3e6ptq1cvx7fw" />
              <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-gray-600" data-id="element-2509" data-mp="3s5zbv6knibmi" />
              <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-gray-600" data-id="element-2510" data-mp="3o3ufi02tj0w6" />

              <div className="flex items-center gap-3 mb-8" data-id="element-2511" data-mp="1xspw4x1ihtvt">
                <div className="w-12 h-12 bg-gray-800 border border-gray-700 flex items-center justify-center flex-shrink-0" data-id="element-2512" data-mp="1tq33wv0xi6y3">
                  <Activity size={24} className="text-gray-400" data-id="element-2513" data-mp="1jlwdno4l0uzg" />
                </div>
                <div className="min-w-0" data-id="element-2514" data-mp="1no9ga3o18cs0">
                  <div className="text-[10px] font-mono text-gray-500 tracking-widest uppercase" data-id="element-2515" data-mp="1nn82vsjjne74">
                    Neurological Mechanism
                  </div>
                  <h3 className="text-xl md:text-2xl font-black text-white uppercase tracking-tight" data-id="element-2516" data-mp="1eumoeomy3to3">
                    The Science
                  </h3>
                </div>
              </div>

              <div className="space-y-6" data-id="element-2517" data-mp="14xiblfezpajy">
                <div data-id="element-2518" data-mp="14yjlvho6lt0m">
                  <h4 className="text-base md:text-lg font-bold text-white mb-4 uppercase tracking-wide" data-id="element-2519" data-mp="qxh0z88xaora">
                    Why You Can't See It:
                  </h4>
                  <ul className="space-y-3" data-id="element-2520" data-mp="vwtwsihrv660">
                    <SciencePoint number="01" text="Your amygdala (threat detector) becomes desensitized to chronic manipulation" data-id="element-2521" data-mp="2layrn44mk1qj" />
                    <SciencePoint number="02" text="Cortisol flooding creates cognitive fog that impairs pattern recognition" data-id="element-2522" data-mp="2pcumfh96ong" />
                    <SciencePoint number="03" text="Intermittent reinforcement (hot/cold cycles) creates addiction-like neural pathways" data-id="element-2523" data-mp="3p4z1upn42gch" />
                  </ul>
                </div>

                <div className="bg-gray-900/50 border border-gray-800 p-4 rounded" data-id="element-2524" data-mp="3g5c208a15ilj">
                  <div className="flex items-center justify-between mb-4" data-id="element-2525" data-mp="3b4egpmtq48ft">
                    <span className="text-xs font-mono text-gray-500 uppercase tracking-widest" data-id="element-2526" data-mp="365tfpl6ndkqb">
                      Threat Level Over Time
                    </span>
                    <TrendingDown size={16} className="text-[#FF0000]" data-id="element-2527" data-mp="32745u2ktilbf" />
                  </div>

                  {/* Simple Data Visualization */}
                  <div className="space-y-3" data-id="element-2528" data-mp="10efz3j8ytfqw">
                    <ProgressBar label="Week 1" percentage={85} color="bg-[#FFD700]" data-id="element-2529" data-mp="10bk1jw114t2z" />
                    <ProgressBar label="Week 4" percentage={60} color="bg-orange-500" data-id="element-2530" data-mp="2n8jlcwljp89p" />
                    <ProgressBar label="Week 12" percentage={30} color="bg-[#FF0000]" data-id="element-2531" data-mp="2w2o8p61h01kh" />
                  </div>

                  <p className="text-xs text-gray-500 mt-4 font-mono leading-relaxed" data-id="element-2532" data-mp="24agro4i1yn5k">
                    Your brain's threat detection decreases as it adapts to
                    chronic toxicity.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom: The Hook */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6,
        delay: 0.6
      }} className="relative" data-id="element-2533" data-mp="3ju3ex9wdb310">
          <div className="bg-gradient-to-r from-[#FF0000]/10 via-[#FF0000]/5 to-[#FF0000]/10 border-l-4 border-[#FF0000] p-6 md:p-8 lg:p-12" data-id="element-2534" data-mp="3tyffyipvym1d">
            <div className="max-w-4xl" data-id="element-2535" data-mp="3186yyu7367od">
              <div className="flex items-start gap-4 md:gap-6 mb-8" data-id="element-2536" data-mp="319hyghti0t9o">
                <div className="flex-shrink-0 w-12 h-12 md:w-16 md:h-16 bg-[#FF0000] flex items-center justify-center text-white font-black text-xl md:text-2xl" data-id="element-2537" data-mp="367t9gvvj59th">
                  !
                </div>
                <div className="min-w-0" data-id="element-2538" data-mp="3f8hkritjgw2o">
                  <h3 className="text-xl sm:text-2xl md:text-3xl font-black text-white mb-4 uppercase tracking-tight leading-tight" data-id="element-2539" data-mp="3f53kcg4fqwxv">
                    You're Not "Just Fighting."
                    <br data-id="element-2540" data-mp="ufe7u9r7fn8c" />
                    <span className="text-[#FF0000]" data-id="element-2541" data-mp="2ez8hsmvzf6q2">
                      You're Being Systematically Drained.
                    </span>
                  </h3>
                  <p className="text-gray-300 text-base md:text-lg leading-relaxed" data-id="element-2542" data-mp="2ay6f1w2uwcjc">
                    Most people don't realize they're victims of emotional
                    manipulation until they've lost their sense of self. The
                    patterns are invisible because{' '}
                    <span className="text-white font-bold" data-id="element-2543" data-mp="1w9jtvdqjsqk7">
                      your brain has been programmed to ignore them
                    </span>
                    .
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-2544" data-mp="1n9wx558rk8xh">
                <StatBox number="73%" label="of victims don't recognize gaslighting until after the relationship ends" data-id="element-2545" data-mp="1iatsvn0ly6qk" />
                <StatBox number="2-5 yrs" label="average time to identify covert narcissistic abuse" data-id="element-2546" data-mp="3co1upyn03ilp" />
                <StatBox number="89%" label="report feeling 'crazy' or doubting their own reality" data-id="element-2547" data-mp="4cxuhx2qej1i" />
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};
// Helper Components
const SciencePoint = ({
  number,
  text
}: {
  number: string;
  text: string;
}) => <li className="flex items-start gap-3" data-id="element-2548" data-mp="jbjhxtiz42zf">
    <span className="flex-shrink-0 w-8 h-8 bg-gray-800 border border-gray-700 flex items-center justify-center text-gray-400 font-mono text-xs" data-id="element-2549" data-mp="j8dqi7mvgp0j">
      {number}
    </span>
    <span className="text-gray-300 text-sm leading-relaxed pt-1.5" data-id="element-2550" data-mp="9bsntsybrpzv">{text}</span>
  </li>;
const ProgressBar = ({
  label,
  percentage,
  color
}: {
  label: string;
  percentage: number;
  color: string;
}) => <div data-id="element-2551" data-mp="2u4clxu6b2qke">
    <div className="flex justify-between text-xs font-mono text-gray-500 mb-2" data-id="element-2552" data-mp="2u4mdhm7015s7">
      <span data-id="element-2553" data-mp="2z2nv02os0x1k">{label}</span>
      <span data-id="element-2554" data-mp="2yz047bapuoqj">{percentage}% awareness</span>
    </div>
    <div className="h-2 bg-gray-800 overflow-hidden rounded-sm" data-id="element-2555" data-mp="2rgio2m8c4bol">
      <motion.div initial={{
      width: 0
    }} whileInView={{
      width: `${percentage}%`
    }} viewport={{
      once: true
    }} transition={{
      duration: 1,
      delay: 0.5,
      ease: 'easeOut'
    }} className={`h-full ${color}`} data-id="element-2556" data-mp="2mj0lddkwusp1" />
    </div>
  </div>;
const StatBox = ({
  number,
  label
}: {
  number: string;
  label: string;
}) => <div className="bg-[#0a0a0a] border border-gray-800 p-4 md:p-5 text-center rounded" data-id="element-2557" data-mp="23tk8rrvyb9s9">
    <div className="text-2xl md:text-3xl font-black text-[#FF0000] mb-2 font-mono" data-id="element-2558" data-mp="2hkpdh8e6es9g">
      {number}
    </div>
    <div className="text-xs text-gray-400 leading-tight font-mono uppercase" data-id="element-2559" data-mp="2cs5pdhhv4eqe">
      {label}
    </div>
  </div>;